import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import AppShell from "../components/layout/AppShell";
import Toast from "../components/ui/Toast";
import { supabase } from "../lib/supabase";
import Dashboard from "../pages/Dashboard/Dashboard";
import LifeBoatPage from "../pages/LifeBoat/LifeBoatPage";
import LifeRaftPage from "../pages/LifeRaft/LifeRaftPage";
import LoginPage from "../pages/Login/LoginPage";
import SettingsPage from "../pages/Settings/SettingsPage";
import UsersPage from "../pages/Users/UsersPage";
import {
  deleteAsset as deleteAssetRecord,
  fetchAssets,
  fetchClients,
  saveAsset as saveAssetRecord,
} from "../services/assetService";
import {
  fetchProfile,
  fetchUsers,
  updatePreferences,
} from "../services/profileService";
import type {
  AlertPreferenceUpdate,
  AppUser,
  Asset,
  AssetDraft,
  ClientOption,
  Profile,
  View,
} from "../types";
import { getUpcomingAlerts, type UpcomingAlert } from "../utils/assetDates";

export default function App() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [view, setView] = useState<View>("Dashboard");
  const [initialising, setInitialising] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState("");
  const [toast, setToast] = useState("");
  const [requestedClientId, setRequestedClientId] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);

  const notify = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 3200);
  }, []);

  const hydrateWorkspace = useCallback(async (userId: string) => {
    setDataLoading(true);
    setDataError("");
    try {
      const [nextProfile, nextAssets, nextClients] = await Promise.all([
        fetchProfile(supabase, userId),
        fetchAssets(supabase),
        fetchClients(supabase),
      ]);
      const nextUsers =
        nextProfile.role === "Admin" ? await fetchUsers(supabase) : [];
      setProfile(nextProfile);
      setAssets(nextAssets);
      setClients(nextClients);
      setUsers(nextUsers);
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;

    async function restoreSession() {
      const rememberSession =
        localStorage.getItem("inscape_remember_session") === "true";
      const currentTabSession =
        sessionStorage.getItem("inscape_current_tab_session") === "true";

      if (!rememberSession && !currentTabSession) {
        await supabase.auth.signOut();
      }

      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      if (data.session?.user && active) {
        await hydrateWorkspace(data.session.user.id);
      }
    }

    void restoreSession()
      .catch(async (error: unknown) => {
        await supabase.auth.signOut();
        if (active) {
          setProfile(null);
          notify(
            error instanceof Error
              ? error.message
              : "Unable to restore the current session.",
          );
        }
      })
      .finally(() => {
        if (active) setInitialising(false);
      });

    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        setProfile(null);
        setAssets([]);
        setClients([]);
        setUsers([]);
        setView("Dashboard");
      }
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    };
  }, [hydrateWorkspace, notify]);

  const reloadWorkspace = useCallback(async () => {
    const { data } = await supabase.auth.getUser();
    if (!data.user) return;
    try {
      await hydrateWorkspace(data.user.id);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to reload INSCAPE data.";
      setDataError(message);
      notify(message);
    }
  }, [hydrateWorkspace, notify]);

  async function handleAuthenticated() {
    const { data, error } = await supabase.auth.getUser();
    if (error) throw error;
    if (!data.user) throw new Error("The sign-in session was not created.");
    await hydrateWorkspace(data.user.id);
    setView("Dashboard");
  }

  async function saveAsset(asset: Asset | AssetDraft) {
    try {
      const saved = await saveAssetRecord(supabase, asset);
      setAssets((current) => {
        const exists = current.some((item) => item.id === saved.id);
        return exists
          ? current.map((item) => (item.id === saved.id ? saved : item))
          : [...current, saved];
      });
      notify("Asset record saved successfully.");
      return true;
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to save the asset.");
      return false;
    }
  }

  async function deleteAsset(asset: Asset) {
    if (!window.confirm(`Delete ${asset.assetId}? This cannot be undone.`)) return;
    try {
      await deleteAssetRecord(supabase, asset);
      setAssets((current) => current.filter((item) => item.id !== asset.id));
      notify("Asset removed from the register.");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to delete the asset.");
    }
  }

  async function saveSettings(update: AlertPreferenceUpdate) {
    if (!profile) return;
    try {
      await updatePreferences(supabase, profile.userId, update);
      setProfile((current) =>
        current
          ? {
              ...current,
              fullName: update.fullName,
              emailAlertsEnabled: update.emailAlertsEnabled,
              alert14Days: update.alert14Days,
              alert7Days: update.alert7Days,
            }
          : null,
      );
      notify("Profile and email preferences saved.");
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Unable to save your settings.",
      );
      throw error;
    }
  }

  function navigate(nextView: View) {
    setRequestedClientId(null);
    setView(nextView);
  }

  function openAlert(alert: UpcomingAlert) {
    setRequestedClientId(alert.asset.clientId);
    setView(alert.asset.type);
  }

  async function logout() {
    localStorage.removeItem("inscape_remember_session");
    sessionStorage.removeItem("inscape_current_tab_session");
    await supabase.auth.signOut();
  }

  const counts = useMemo(
    () => ({
      raft: assets.filter((asset) => asset.type === "Life Raft").length,
      boat: assets.filter((asset) => asset.type === "Life Boat").length,
    }),
    [assets],
  );
  const alerts = useMemo(() => getUpcomingAlerts(assets), [assets]);

  if (initialising) {
    return (
      <main className="screen-loader">
        <div>
          <i /> Loading INSCAPE…
        </div>
      </main>
    );
  }

  if (!profile) {
    return (
      <>
        <LoginPage onAuthenticated={handleAuthenticated} />
        <Toast message={toast} />
      </>
    );
  }

  const activeView = profile.role === "User" && view === "Users" ? "Dashboard" : view;
  const moduleProps = {
    assets,
    clients,
    loading: dataLoading,
    error: dataError,
    requestedClientId,
    onSave: saveAsset,
    onDelete: deleteAsset,
    onReload: reloadWorkspace,
  };

  return (
    <>
      <AppShell
        view={activeView}
        profile={profile}
        counts={counts}
        alerts={alerts}
        onNavigate={navigate}
        onOpenAlert={openAlert}
        onLogout={() => void logout()}
      >
        {activeView === "Dashboard" && (
          <Dashboard
            assets={assets}
            name={profile.fullName}
            role={profile.role}
            navigate={navigate}
          />
        )}
        {activeView === "Life Raft" && <LifeRaftPage {...moduleProps} />}
        {activeView === "Life Boat" && <LifeBoatPage {...moduleProps} />}
        {activeView === "Users" && profile.role === "Admin" && (
          <UsersPage users={users} clients={clients} />
        )}
        {activeView === "Settings" && (
          <SettingsPage profile={profile} onSave={saveSettings} />
        )}
      </AppShell>
      <Toast message={toast} />
    </>
  );
}
